import { Tooltip, Typography } from "@material-ui/core";
import { useMemo } from "react";
import Image from "../elements/Image";
import Statistic, { StatThresholds } from "../elements/Statistic";
import Table from "../tables/Table";
import ChartGrowerMoisture from "../charts/ChartGrowerMoisture";
import { useLocalStorage } from "../../helpers/useLocalStorage";
import { useExtendedTheme } from "../../helpers/useExtendedTheme";
import { capitalizeFirstLetter as capitalise } from "../../helpers";
import moment, { Moment } from "moment-timezone";
import { useColumnVisibility } from "../../helpers/useColumnVisibility";
import { useAuthState } from "../../context/auth";
import { fieldNameSorter } from "../../helpers/sorting";

const gaugeRender = (
  row: { name: string; data: { value: number }[]; parameters: unknown },
  isSoil: boolean
) => {
  return (
    <ChartGrowerMoisture
      id={row.name}
      level={row.data[0].value}
      isSoil={isSoil}
      paramData={row.parameters}
    />
  );
};

const FieldsTable = ({
  fields,
  title,
  onClick,
  fieldType,
  clientName,
}: {
  fields: {
    id: number;
    name: string;
    data: Record<string, { short_name: string; ref: string }>;
  }[];
  title: string;
  onClick: (id: number) => void;
  fieldType: "soil" | "substrate";
  clientName: string;
}) => {
  const theme = useExtendedTheme();
  const user = useAuthState();
  const { isVisible } = useColumnVisibility(user);
  const columns = useMemo(() => {
    const ageLimit = moment().subtract(1, "month");
    const userTableColumns =
      fields && fields.length && fields[0].data && fields[0].data.length
        ? Object.keys(fields[0].data).map((metric) => {
            return {
              title: fields[0].data[metric].short_name,
              field: fields[0].data[metric].ref,
              render: ({ data, parameters }: any) => {
                const { ref, value, suffix, thresholds, timestamp } = data[
                  metric
                ] as {
                  ref: string;
                  value: number | null;
                  suffix?: string;
                  thresholds: StatThresholds;
                  timestamp?: Moment;
                };

                if (timestamp?.isBefore(ageLimit)) {
                  return (
                    <Tooltip
                      title={`Last data received ${timestamp.fromNow()}`}
                      placement="top"
                    >
                      <span>
                        <Statistic
                          {...{
                            type: ref,
                            value: null,
                            suffix,
                            thresholds,
                            parameters,
                          }}
                        />
                      </span>
                    </Tooltip>
                  );
                } else if (timestamp) {
                  return (
                    <Tooltip
                      title={timestamp.format("DD/MM/YYYY HH:mm")}
                      placement="top"
                    >
                      <span>
                        <Statistic
                          {...{
                            type: ref,
                            value,
                            suffix,
                            thresholds,
                            parameters,
                          }}
                        />
                      </span>
                    </Tooltip>
                  );
                }

                return (
                  <Statistic
                    {...{ type: ref, value, suffix, thresholds, parameters }}
                  />
                );
              },
              customSort: (a: any, b: any) => {
                // check if data is stale
                const aTimestamp = a.data[metric]?.timestamp;
                const bTimestamp = b.data[metric]?.timestamp;
                const aIsStale = aTimestamp
                  ? aTimestamp.isBefore(ageLimit)
                  : true;
                const bIsStale = bTimestamp
                  ? bTimestamp.isBefore(ageLimit)
                  : true;

                if (aIsStale && bIsStale) {
                  return 0; // both stale, consider equal
                } else if (aIsStale) {
                  return 1; // a is stale, b is not, b comes first
                } else if (bIsStale) {
                  return -1; // b is stale, a is not, a comes first
                } else {
                  // both fresh, sort by value
                  const aValue = a.data[metric]?.value ?? -Infinity;
                  const bValue = b.data[metric]?.value ?? -Infinity;
                  return bValue - aValue;
                }
              },
            };
          })
        : [];

    const soilColumnOrder = [
      "moisture_content",
      "smd",
      "total_24hr_gains",
      "total_24hr_ml_in",
      "total_24hr_ml_out",
      "irri_trigger",
      "predicted_irri",
      "rainfall",
      "ambient_temp",
      "humidity",
      "delta_t",
    ];
    const orderedUserTableColumns =
      fieldType === "soil"
        ? soilColumnOrder.reduce((arr: typeof userTableColumns, ref) => {
            const col = userTableColumns.find(({ field }) => field === ref);
            return col ? [...arr, col] : arr;
          }, [])
        : userTableColumns;

    const defaultColumns = [
      {
        title: "Name",
        field: "name",
        cellStyle: {
          textTransform: "uppercase" as const,
          fontWeight: "bold" as const,
          color: theme.palette.tertiary.main,
        },
        render: ({ name }: { name: string }) => name,
        customSort: fieldNameSorter,
      },
    ];

    const moistureGauge = [
      {
        title: "",
        align: "center" as const,
        field: "moistureguage",
        render: (row: any) => {
          if (row.irrimax) {
            return (
              <Image src={row.irrimax[0].gauge} alt={row.irrimax[0].name} />
            );
          }
          return (
            row.data?.length > 0 &&
            row.data[0]?.value > 0 &&
            gaugeRender(row, fieldType === "soil")
          );
        },
      },
    ];

    const result =
      fields && fields.length
        ? [...defaultColumns, ...moistureGauge, ...orderedUserTableColumns]
        : defaultColumns;
    return result.filter((col) => isVisible(col.field));
  }, [fieldType, fields, theme.palette.tertiary.main, isVisible]);

  const [pageSize, setPageSize] = useLocalStorage(
    `${fieldType}FieldTablePageSize`,
    5,
    true
  );

  return (
    <Table
      columns={columns}
      data={fields}
      options={{
        pageSize,
        pageSizeOptions: [5, 10, 20, 50],
        headerStyle: { position: "sticky", top: 0 },
        maxBodyHeight: "800px",
      }}
      title={
        <Typography variant="h2" color="primary">
          {title}
        </Typography>
      }
      onRowClick={(_event: any, row) => row && onClick(row.id)}
      onRowsPerPageChange={setPageSize}
      columnStorageKey={`${clientName}${capitalise(
        fieldType
      )}FieldTableColumns`}
    />
  );
};

export default FieldsTable;
